import { Fragment, ReactNode } from 'react';

import { ImageCard, ImageCardProps } from '@backrs_ui/common-ui';

import { Box, Theme, SxProps } from '@mui/material';

import { ActionStickerCardSectionDisplay } from './ActionStickerCardSection';
import { SectionHeader } from '../atoms/SectionHeader';
import { ActionStickerCardSection } from '../hooks/useGetHomePageSections';

export type HomePageSectionProps = Readonly<{
  title: string;
  description: string;

  content:
    | {
        type: 'ACTION_STICKER';
        actionStickerSections: ActionStickerCardSection[];
      }
    | {
        type: 'IMAGE_CARD';
        imageCardSections: ImageCardProps[];
      }
    | {
        type: 'ELEMENT';
        elements: ReactNode[];
      };
}>;

export const HomePageSection = ({ title, description, content }: HomePageSectionProps) => {
  return (
    <>
      <SectionHeader title={title} description={description} />

      {/* ACTION STICKER SECTIONS IF PROVIDED */}
      {content.type === 'ACTION_STICKER' && (
        <Box sx={actionSectionContainerSx}>
          {content.actionStickerSections.map((actionStickerSection) => (
            <ActionStickerCardSectionDisplay
              key={actionStickerSection.title}
              actionStickerSection={actionStickerSection}
            />
          ))}
        </Box>
      )}

      {/* IMAGE CARD SECTIONS IF PROVIDED */}
      {content.type === 'IMAGE_CARD' && (
        <Box sx={imageCardSectionContainer}>
          {content.imageCardSections.map((imageCard) => (
            <ImageCard
              key={imageCard.title}
              title={imageCard.title}
              description={imageCard.description}
              imageUrl={imageCard.imageUrl}
              onClick={imageCard.onClick}
            />
          ))}
        </Box>
      )}

      {/* ELEMENT CARD SECTION */}
      {content.type === 'ELEMENT' && (
        <Box sx={elementContainerSx}>
          {content.elements.map((section, index) => (
            <Fragment key={index}>{section}</Fragment>
          ))}
        </Box>
      )}
    </>
  );
};

const elementContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  marginLeft: {
    sm: backrs.spacing['spacing-lg'],
  },
  gap: {
    xs: backrs.spacing['spacing-2xl'],
    md: backrs.spacing['spacing-4xl'],
  },
});

const actionSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: {
    sm: backrs.spacing['spacing-3xl'],
  },
  boxSizing: 'border-box',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const imageCardSectionContainer: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
  gap: backrs.spacing['spacing-4xl'],
  alignItems: {
    xs: 'center',
    sm: 'stretch',
  },
  marginLeft: {
    sm: backrs.spacing['spacing-lg'],
  },
});
